import moment from 'moment'
import i18n from 'translation/index'
import { openErrorToaster } from 'utils/common-services/toaster.service'
import { divide, times } from 'number-precision'
import store from 'redux-store/store'

export const DEFAULT_PRECISION_MONEY = 2

export const formatDate = (originalDateString) => {
  const date = new Date(originalDateString)

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ]
  const day = date.getDate()
  const monthIndex = date.getMonth()
  const year = date.getFullYear()
  const formattedDate = `${day} ${months[monthIndex]} ${year}`
  return formattedDate
}

export const formatTime = (originalDateString) => {
  const date = new Date(originalDateString)

  const hours = date.getHours()
  const minutes = date.getMinutes()

  const ampm = hours >= 12 ? 'PM' : 'AM'
  const formattedHours = hours % 12 || 12 // Convert 0 to 12 for 12 AM

  const formattedTime = `${formattedHours}:${minutes < 10 ? '0' : ''}${minutes} ${ampm}`

  return formattedTime
}

/**
 * Parses payload object from jwt
 * @param {string} token
 * @returns {Object}
 */
export const getPayloadFromToken = (token) => {
  if (token) {
    return JSON.parse(atob(token.split('.')[1]))
  }
}

/**
 * Generate a random card number between [1,52]
 * @returns {number}
 */
export const randomCardGenerate = () => {
  return Math.floor(Math.random() * 52) + 1
}

/**
 *
 * @param {Number} n
 * @returns {Number} factorial of n
 */
export const factorial = n => {
  let fact = 1
  for (let i = 2; i <= n; i++) {
    fact *= i
  }
  return fact
}

/**
 *
 * @param {Number} n
 * @param {Number} r
 * @returns {Number} combination of n over r
 */
export const combination = (n, r) => {
  if (r > n) {
    return 0
  }
  return factorial(n) / (factorial(r) * factorial(n - r))
}

/**
 *
 * @param {Number} numberOfCoins
 * @param {Number} minimumChosenOutcome
 * @returns {Number} probability of minimum X number of favorable outcomes
 */
export const getCoinOutcomeProbability = (numberOfCoins, minimumChosenOutcome) => {
  const n = numberOfCoins - minimumChosenOutcome
  let sum = 0
  for (let i = 0; i <= n; i++) {
    sum += combination(numberOfCoins, i)
  }
  return sum / 2 ** numberOfCoins
}

/**
 * To calculate flip coin potential win
 * @param {number} betAmount
 * @param {number} numberOfCoins
 * @param {number} minimumChosenOutcome
 * @param {number} houseEdge
 * @returns {number}
 */
export const getFlipCoinPotentialWin = (betAmount, numberOfCoins, minimumChosenOutcome, houseEdge) => {
  const probability = getCoinOutcomeProbability(numberOfCoins, minimumChosenOutcome)
  const odds = (1 / probability) * (1 - houseEdge / 100)
  return +Number((betAmount * odds)).toFixed(2)
}

/**
 *
 * @param {string} s
 * @returns {Number} count of one
 */
export const countOnes = (s) => {
  let count = 0
  for (const i of s) {
    if (i === '1') {
      count++
    }
  }
  return count
}

/**
 *
 * @param  {...Array} a Specify arrays to do cartesian product
 * @returns {Array.<Array.<number[]>>} Returns the cartesian product of given arrays
 */
export const cartesianProductOfArrays = (...a) => a.reduce((a, b) => a.flatMap(d => b.map(e => [d, e].flat())))

export function generateClientSeed () {
  const availableChars = '0123456789abcdef'
  let seed = ''
  for (let i = 0; i < 32; i++) {
    seed += availableChars[Math.floor(Math.random() * availableChars.length)]
  }
  return seed
}

/**
 *
 * @param {number} value
 * @param {number} precision
 * @returns {number | string} Returns precision value for specified decimal places
 */
export const getPrecision = (value, precision = 2) => {
  const result = Math.trunc(value * Math.pow(10, precision)) / Math.pow(10, precision)
  return result || 0
}

export const getPrecisionVal = (value, precision) => {
  // const { precisionValue } = usePrecisionValueController()
  if (typeof value === 'string' && value === '') return ''
  const numericValue = typeof value === 'string' ? Number(value) : value // Parse value if it's a string, safely convert to number

  const precisionVal = precision ?? store?.getState()?.gameSetting?.currencyPrecision ?? 2

  // const state = store.getState()
  // const precisionVal = precisionValue

  if (!Number.isFinite(numericValue)) return typeof value === 'string' ? `0.${'0'.repeat(precisionVal)}` : 0 // Edge case: handle invalid values like NaN, undefined, Infinity safely

  if (typeof value === 'string' && value === '0') return `0.${'0'.repeat(precisionVal)}`

  const precisionDivide = Math.pow(10, precisionVal)
  let result = divide(Math.trunc(times(numericValue, precisionDivide)), precisionDivide)

  // result = result.toLocaleString('fullwide', { useGrouping: false, maximumFractionDigits: precisionVal })
  if (result.toString().includes('e')) result = result.toFixed(precisionVal)

  return result
}

/**
 * To convert a numeric value to standard precision
 * @param {number} amount amount to be converted to precision
 * @param {number} precision digits to be considered for precision
 * @returns {number}
 */
export const getPrecisionNumber = (amount, precision = DEFAULT_PRECISION_MONEY) => {
  const precisionDivide = 10 ** precision
  const result = (parseInt(amount * precisionDivide) / precisionDivide) || 0
  return result
}

/**
 *
 * @param {number} x
 * @returns {string} valid million based comma value in string
 */
export const numberWithCommas = (x) => {
  if (x === null || x === undefined || Number(x) === Number.isNaN) {
    return ''
  }
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * Get Random Integer from min to max (both inclusive)
 * @param {*} max
 * @returns
 */
export function getRandomInt (min, max) {
  min = Math.ceil(min)
  max = Math.floor(max + 1)
  return Math.floor(Math.random() * (max - min) + min)
}
/**
 * Durstenfeld array shuffling algorithm
 * @param {number} array
 */
export const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]]
  }
  return array
}

export const copyToClipboard = (text) => {
  if (window.clipboardData && window.clipboardData.setData) {
    // Internet Explorer-specific code path to prevent textarea being shown while dialog is visible.
    return window.clipboardData.setData('Text', text)
  }
  if (document.queryCommandSupported && document.queryCommandSupported('copy')) {
    const textarea = document.createElement('textarea')
    textarea.textContent = text
    textarea.style.position = 'fixed' // Prevent scrolling to bottom of page in Microsoft Edge.
    document.body.appendChild(textarea)
    textarea.select()
    try {
      return document.execCommand('copy') // Security exception may be thrown by some browsers.
    } catch (ex) {
      console.warn('Copy to clipboard failed.', ex)
      return false
    } finally {
      document.body.removeChild(textarea)
    }
  }
}

export const blockInvalidChar = (e) => {
  ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()
  if (!/[\d]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete') {
    event.preventDefault()
  }
}

export const blockSpaceChar = e => e.key === ' ' && e.preventDefault()

export const formatDateTime = (dateString) => {
  // Create a new Date object from the given date string
  const date = new Date(dateString)

  // Extract hours, minutes, and determine AM/PM
  let hours = date.getUTCHours()
  const minutes = date.getUTCMinutes()
  const ampm = hours >= 12 ? 'PM' : 'AM'

  // Convert 24-hour format to 12-hour format
  hours = hours % 12
  hours = hours || 12 // If hours equals 0, change it to 12

  // Format minutes to be 2 digits
  const minutesFormatted = minutes < 10 ? '0' + minutes : minutes

  // Extract month, day, and year
  const month = date.getUTCMonth() + 1 // Months are zero-indexed
  const day = date.getUTCDate()
  const year = date.getUTCFullYear()

  // Return formatted string
  return `${hours}:${minutesFormatted} ${ampm}  ${month}/${day}/${year}`
}

export const toUtcTime = (dateObject = moment(), dateFormat = 'DD/MM/YYYY') => {
  return moment.utc(dateObject).format(dateFormat)
}

export const showToastToLogin = () => {
  openErrorToaster({ message: i18n.t('LOGIN_REQUIRED') })
}
